import { useRef, useState } from 'react';

import { IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Typography } from '@cfa/react-components';

import { cn } from '@/utils/tailwind';

import PopoverMenuButton from 'components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from 'components/PopoverMenuButton/PopoverMenuButtonItem';
import { itemByLanguage } from 'utils/language';

import EditButtons from './EditButtons';
import EditSectionName from './EditSectionName';
import SixDotIcon from './SixDotIcon';

import { PlanSection } from '@/_generated/api-clients/xp';

interface TitleProps {
  deleteSectionPopUp: (sectionId: string, sectionName: string) => void;
  editSectionTitle?: (section: PlanSection) => boolean;
  index: number;
  isAllowedToEdit: boolean;
  isPreview?: boolean;
  onCancel: () => void;
  onEdit: (title: string) => void;
  renameSectionPopUp?: (sectionId: string) => void;
  section: PlanSection;
  isReadOnly?: boolean;
}

const Title: React.FC<TitleProps> = ({
  section,
  isAllowedToEdit,
  editSectionTitle,
  renameSectionPopUp,
  deleteSectionPopUp,
  onCancel,
  onEdit,
  isPreview,
  index,
  isReadOnly = false,
}) => {
  const threeDotRef = useRef(null);
  const { t } = useTranslation();
  const [updatedSectionTitle, setUpdatedSectionTitle] = useState('');

  const isEditMode = editSectionTitle ? editSectionTitle(section) : false;

  return (
    <div
      className={cn(
        'flex w-full flex-row gap-4',
        isEditMode ? 'items-start' : 'items-center',
      )}
    >
      {!isPreview && !isReadOnly && (
        <div className="section-drag-handle">
          <SixDotIcon position={isEditMode ? 'top' : 'center'} />
        </div>
      )}
      <div
        className={cn('flex w-full', {
          'mt-2': isPreview,
        })}
      >
        <Typography
          className="text-grayscale-gray6! line-clamp-1! w-full! overflow-hidden!"
          data-testid={`Title-${index}`}
          fontWeight="bold"
          variant="body1"
        >
          {(!!isPreview || !isEditMode) && itemByLanguage(section.name)}
          {!isPreview && isAllowedToEdit && isEditMode && (
            <div className="flex w-full flex-col items-center justify-between">
              <EditSectionName
                onEnter={() => {
                  if (updatedSectionTitle.trim().length > 0) {
                    onEdit(updatedSectionTitle);
                  }
                }}
                originalSectionTitle={itemByLanguage(section.name, '')}
                refSectionTitle={threeDotRef}
                updateSectionTitle={updatedTitle =>
                  setUpdatedSectionTitle(updatedTitle)
                }
              />
              <EditButtons
                onCancel={onCancel}
                onEdit={() => onEdit(updatedSectionTitle)}
                validated={updatedSectionTitle.trim().length > 0}
              />
            </div>
          )}
        </Typography>
      </div>
      {!isEditMode && !isPreview && !isReadOnly && (
        <PopoverMenuButton data-testid="SectionMenu">
          <PopoverMenuButtonItem
            dataTestId="ThreeDotMenuPlanPopup"
            icon={<IconEdit />}
            onClick={() => {
              if (!section.id) {
                console.error('No section id was found');
                return;
              }
              renameSectionPopUp?.(section.id);
            }}
            text={t('TrainingPlans.manageThreeDotMenu.editSection')}
          />
          <PopoverMenuButtonItem
            dataTestId="ThreeDotMenuPlanPopup"
            icon={<IconTrash data-testid="SectionDelete" />}
            isDestructive={true}
            onClick={() => {
              if (!section.id) {
                console.error('No section id was found');
                return;
              }
              deleteSectionPopUp(section.id, itemByLanguage(section.name, ''));
            }}
            text={t('TrainingPlans.manageThreeDotMenu.deleteSection')}
          />
        </PopoverMenuButton>
      )}
    </div>
  );
};

export default Title;
